import { Box, Stepper } from '@mantine/core'
import { useSetState } from '@mantine/hooks'
import { uniq } from 'lodash-es'
import { useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import { getApiError } from '@/api/helpers/apiError'
import { LoadingOverlay } from '@/components/ui-shared/LoadingOverlay/LoadingOverlay'
import { useApplicationContext } from '@/providers/ApplicationContext'
import {
  useCreateDeployment,
  useGetDeploymentPlan,
  useInvalidateDeploymentList,
  useUpdateDeployment
} from '@/queries/deploymentQueries'
import { useGetMultipleDeviceDetails } from '@/queries/deviceQueries'
import { RouterPath } from '@/router/paths'
import { showToast } from '@/theme/notifications'
import { Application, ApplicationType } from '@/types/app'
import { NormalizedStreamBusinessLogic } from '@/types/businessLogic'
import { CameraStreamWithDeviceId } from '@/types/camera'
import {
  BlurOptions,
  ConfidenceThreshold,
  EventDestination,
  EventEndpoint,
  GpuSelection,
  InputSourceType,
  MediaFolderConfig,
  OcrSettings
} from '@/types/deployment'
import { MLModel, MLModelType } from '@/types/model'
import { NotificationSettings } from '@/types/notificationSettings'
import { getBrowserTimezone } from '@/utils/date'
import { DeployPreview } from './DeployPreview/DeployPreview'
import {
  DeploymentSettings,
  FormValues as DeploymentSettingsFormValues
} from './DeploymentSettings/DeploymentSettings'
import { DeploymentStepperProvider } from './DeploymentStepperContext'
import { DeviceSelection } from './DeviceSelection/DeviceSelection'
import {
  FormValues as InputSourceFormValues,
  InputSourceSelection
} from './InputSourceSelection/InputSourceSelection'
import { IntegrationSelection } from './IntegrationSelection/IntegrationSelection'
import {
  DEFAULT_EVENT_VIDEO_DURATION_TIME,
  DEFAULT_INFERENCE_INTERVAL,
  DEFAULT_MIN_DETECTION_FRAMES,
  DEFAULT_PRE_EVENT_SECONDS,
  FRAME_HEIGHT,
  FRAME_WIDTH
} from './LogicSelection/LogicHandler/constants'
import { LogicSelection } from './LogicSelection/LogicSelection'
import { ModelSelection } from './ModelSelection/ModelSelection'
import { StepNumber } from './StepNumber'
import { buildDeploymentData } from './helpers/buildDeploymentData'
import { RunningGpus, pruneGpuSelection } from './helpers/gpuSelection'

type DeploymentHandlerState = {
  selectedPrimaryModel: MLModel | null
  selectedSecondaryModel: MLModel | null
  selectedDeviceIds: string[]
  selectedCameraStreams: CameraStreamWithDeviceId[]
  savedStreamLogics: NormalizedStreamBusinessLogic[]
  savedEventEndpoints: EventEndpoint[]
  confidenceThresholds: ConfidenceThreshold[]
  blurOptions: BlurOptions
  eventVideoDurationTime: number
  preEventSeconds: number
  cacheSeconds: number | ''
  inferenceInterval: number
  minDetectionFrames: number
  notificationSettings: NotificationSettings
  ocrSettings: OcrSettings
  inputSource: InputSourceType
  mediaFolderConfig: MediaFolderConfig
  gpuIndex: number | null
  runInGroups: boolean
  gpuSelection: GpuSelection
}

type DeploymentHandlerProps = {
  mode: 'create' | 'edit'
  initialStep?: number
  initialPrimaryModel?: MLModel
  initialSecondaryModel?: MLModel
  initialDeviceIds?: string[]
  initialCameraStreams?: CameraStreamWithDeviceId[]
  initialStreamLogics?: NormalizedStreamBusinessLogic[]
  initialEventEndpoints?: EventEndpoint[]
  initialConfidenceThresholds?: ConfidenceThreshold[]
  initialBlurOptions?: BlurOptions
  initialEventVideoDurationTime?: number
  initialPreEventSeconds?: number
  initialCacheSeconds?: number | null
  initialInferenceInterval?: number
  initialMinDetectionFrames?: number
  initialNotificationSettings?: NotificationSettings
  initialOcrSettings?: OcrSettings
  initialInputSource?: InputSourceType
  initialMediaFolderConfig?: MediaFolderConfig
  initialGpuIndex?: number | null
  initialRunInGroups?: boolean
  initialGpuSelection?: GpuSelection
  initialRunningGpus?: RunningGpus
}

export const DeploymentHandler = ({
  mode,
  initialStep,
  initialPrimaryModel,
  initialSecondaryModel,
  initialDeviceIds,
  initialCameraStreams,
  initialStreamLogics,
  initialEventEndpoints,
  initialConfidenceThresholds,
  initialBlurOptions,
  initialEventVideoDurationTime,
  initialPreEventSeconds,
  initialCacheSeconds,
  initialInferenceInterval,
  initialMinDetectionFrames,
  initialNotificationSettings,
  initialOcrSettings,
  initialInputSource,
  initialMediaFolderConfig,
  initialGpuIndex,
  initialRunInGroups,
  initialGpuSelection,
  initialRunningGpus
}: DeploymentHandlerProps) => {
  const intl = useIntl()
  const navigate = useNavigate()

  const { application } = useApplicationContext()
  const { application_type = ApplicationType.SingleStage } =
    application as Application // TODO: remove test value after API integration

  const isMultiStage = application_type === ApplicationType.MultiStage

  const [step, setStep] = useState(initialStep ?? 0)

  const [state, setState] = useSetState<DeploymentHandlerState>({
    selectedPrimaryModel: initialPrimaryModel || null,
    selectedSecondaryModel: initialSecondaryModel || null,
    selectedDeviceIds: initialDeviceIds || [],
    selectedCameraStreams: initialCameraStreams || [],
    savedStreamLogics: initialStreamLogics || [],
    savedEventEndpoints: initialEventEndpoints || [
      {
        service: EventDestination.Visionplatform
      }
    ],
    confidenceThresholds: initialConfidenceThresholds || [],
    blurOptions: initialBlurOptions || {
      blur_face: false,
      blur_lp: false,
      blur_label_ids: [],
      blur_transparency: 0
    },
    eventVideoDurationTime:
      initialEventVideoDurationTime ?? DEFAULT_EVENT_VIDEO_DURATION_TIME,
    preEventSeconds: initialPreEventSeconds ?? DEFAULT_PRE_EVENT_SECONDS,
    cacheSeconds: initialCacheSeconds ?? '',
    inferenceInterval: initialInferenceInterval ?? DEFAULT_INFERENCE_INTERVAL,
    minDetectionFrames:
      initialMinDetectionFrames ?? DEFAULT_MIN_DETECTION_FRAMES,
    notificationSettings: initialNotificationSettings
      ? {
          ...initialNotificationSettings,
          timezone: initialNotificationSettings.is_default
            ? getBrowserTimezone()
            : initialNotificationSettings.timezone
        }
      : {
          is_default: true,
          workspace_users: [],
          schedules: [],
          timezone: getBrowserTimezone()
        },
    ocrSettings: initialOcrSettings || {
      whitelist: [],
      blacklist: [],
      detector: false
    },
    gpuIndex: initialGpuIndex ?? null,
    runInGroups: initialRunInGroups ?? false,
    gpuSelection: initialGpuSelection ?? {},
    inputSource: initialInputSource || InputSourceType.Cameras,
    mediaFolderConfig: initialMediaFolderConfig || {
      input_folder_path: '',
      output_folder_path: '',
      delete_processed: false
    }
  })

  const { mutateAsync: createDeployment, isPending: isCreatePending } =
    useCreateDeployment()
  const { mutateAsync: updateDeployment, isPending: isUpdatePending } =
    useUpdateDeployment()

  const { invalidateDeploymentList } = useInvalidateDeploymentList()

  const isPending = isCreatePending || isUpdatePending

  // We show OCR settings only if at least one label group has OCR turned on.
  // Sadly, we have to go through a deeply nested structure to check this.
  // The good news is that each list usually has 1 to 5 items, so it shouldn't slow things down much (I hope!)
  const hasOCREnabled = useMemo(
    () =>
      state.savedStreamLogics.some((stream) =>
        stream.parameters.some((parameter) =>
          parameter.rules.some((rule) =>
            rule.label_groups.some((labelGroup) => labelGroup.is_ocr_enabled)
          )
        )
      ),
    [state.savedStreamLogics]
  )

  const hasEmailAlertEnabled = useMemo(
    () =>
      state.savedStreamLogics.some((stream) =>
        stream.parameters.some((parameter) => parameter.notification)
      ),
    [state.savedStreamLogics]
  )

  const deploymentData = useMemo(
    () =>
      buildDeploymentData({
        appId: application?.id ?? '',
        primaryModelId: state.selectedPrimaryModel?.id || '',
        secondaryModelId: state.selectedSecondaryModel?.id,
        width: FRAME_WIDTH,
        height: FRAME_HEIGHT,
        cameraStreams: state.selectedCameraStreams,
        savedStreamLogics: state.savedStreamLogics.filter(
          (logic) => logic.parameters.length > 0
        ),
        savedEventEndpoints: state.savedEventEndpoints,
        confidenceThresholds: state.confidenceThresholds,
        blurOptions: state.blurOptions,
        eventVideoDurationTime: state.eventVideoDurationTime,
        preEventSeconds: state.preEventSeconds,
        cacheSeconds: state.cacheSeconds,
        inferenceInterval: state.inferenceInterval,
        minDetectionFrames: state.minDetectionFrames,
        notificationSettings: state.notificationSettings,
        ocrSettings: state.ocrSettings,
        inputSource: state.inputSource,
        gpuIndex: state.gpuIndex,
        runInGroups: state.runInGroups,
        gpuSelection: state.gpuSelection,
        mediaFolderConfig: state.mediaFolderConfig,
        selectedDeviceIds: state.selectedDeviceIds
      }),
    [application?.id, state]
  )

  const previewStepIndex = isMultiStage ? 7 : 6
  const isPlanEnabled =
    step === previewStepIndex &&
    state.inputSource === InputSourceType.Cameras &&
    state.runInGroups

  const {
    data: plan,
    isFetching: isPlanLoading,
    error: planError,
    refetch: refetchPlan
  } = useGetDeploymentPlan(
    deploymentData,
    { mode: mode === 'create' ? 'create' : 'update' },
    { enabled: isPlanEnabled }
  )

  const { data: devices } = useGetMultipleDeviceDetails(
    state.selectedDeviceIds,
    isPlanEnabled
  )

  const goNextStep = () => {
    setStep((prev) => prev + 1)
  }

  const goPrevStep = () => {
    setStep((prev) => prev - 1)
  }

  const handlePrimaryModelSelection = (model: MLModel) => {
    setState({ selectedPrimaryModel: model })
    goNextStep()
  }

  const handleSecondaryModelSelection = (model: MLModel) => {
    setState({ selectedSecondaryModel: model })
    goNextStep()
  }

  const handleDeviceSelectionChange = (deviceIds: string[]) => {
    setState((current) => ({
      selectedDeviceIds: deviceIds,

      // Reset GPU index when the device changes or multiple devices are selected,
      // since the previous GPU may not exist on the new device and multi-device
      // deployments don't support GPU selection.
      gpuIndex:
        deviceIds.length > 1 || deviceIds[0] !== current.selectedDeviceIds[0]
          ? null
          : current.gpuIndex,

      gpuSelection: pruneGpuSelection(current.gpuSelection, deviceIds),

      // Remove previously selected camera streams that are not linked to the chosen devices
      selectedCameraStreams: current.selectedCameraStreams.filter((stream) =>
        deviceIds.includes(stream.device_id)
      )
    }))

    goNextStep()
  }

  const handleInputSourceSelection = ({
    inputSource,
    selectedCameras,
    mediaFolderConfig
  }: InputSourceFormValues) => {
    const isMediaFolder = inputSource === InputSourceType.MediaFolder

    // When switching to media folder, we reset cameras, business logic, and integrations
    // since none of these are supported for media folder deployments.
    // We also reset blur_face/blur_lp to prevent stale values from being submitted,
    // as standard blur options are hidden and unavailable for media folder pipelines.
    setState((current) => ({
      inputSource,
      selectedCameraStreams: isMediaFolder ? [] : selectedCameras,
      // For camera, remove any business logics from cameras that are no longer selected.
      savedStreamLogics: isMediaFolder
        ? []
        : current.savedStreamLogics.filter((logic) =>
            selectedCameras.some(
              (stream) => stream.camera_id === logic.camera_id
            )
          ),
      savedEventEndpoints: isMediaFolder ? [] : current.savedEventEndpoints,
      mediaFolderConfig: isMediaFolder
        ? mediaFolderConfig
        : {
            input_folder_path: '',
            output_folder_path: '',
            delete_processed: false
          },
      blurOptions: isMediaFolder
        ? { ...current.blurOptions, blur_face: false, blur_lp: false }
        : current.blurOptions
    }))

    goNextStep()
  }

  const handleStreamLogicsSave = (logics: NormalizedStreamBusinessLogic[]) => {
    setState({ savedStreamLogics: logics })
  }

  const handleIntegrationsSave = (integrations: EventEndpoint[]) => {
    setState({
      savedEventEndpoints: integrations
    })

    goNextStep()
  }

  const handleSettingsSave = (settings: DeploymentSettingsFormValues) => {
    setState(settings)
    goNextStep()
  }

  const handleDeployModel = async () => {
    if (!application) {
      return
    }

    try {
      const { plan: deployedPlan } = await (mode === 'create'
        ? createDeployment(deploymentData)
        : updateDeployment(deploymentData))

      if (deployedPlan?.groups_enabled) {
        showToast(
          intl.formatMessage(
            { id: 'deployments.plan.deployedToast' },
            {
              summary: intl.formatMessage(
                { id: 'deployments.plan.summary' },
                deployedPlan.summary
              )
            }
          ),
          'info'
        )
      }

      void invalidateDeploymentList()
      void navigate(RouterPath.deployments)
    } catch (err) {
      const { errorMessage } = getApiError(err)
      const message =
        errorMessage || intl.formatMessage({ id: 'deployments.error' })
      showToast(message, 'error')
    }
  }

  return (
    <Box pos="relative">
      <LoadingOverlay
        visible={isPending}
        message={<FormattedMessage id="deployments.inProgress" />}
      />
      <DeploymentStepperProvider
        appType={application_type}
        primaryModel={state.selectedPrimaryModel}
        secondaryModel={state.selectedSecondaryModel}
      >
        <Stepper
          active={step}
          size="sm"
          iconSize={28}
          color="green"
          styles={(theme) => ({
            steps: {
              marginBottom: theme.spacing.xl
            }
          })}
        >
          <Stepper.Step
            icon={<StepNumber isMultiStage={isMultiStage} baseIndex={0} />}
            label={
              isMultiStage ? (
                <FormattedMessage id="deployments.step1a" />
              ) : (
                <FormattedMessage id="deployments.step1" />
              )
            }
          >
            <ModelSelection
              key="primary-model"
              title={
                isMultiStage ? (
                  <FormattedMessage id="deployments.step1a" />
                ) : (
                  <FormattedMessage id="deployments.step1" />
                )
              }
              initialSelectedModel={state.selectedPrimaryModel}
              modelType={MLModelType.Primary}
              onContinue={handlePrimaryModelSelection}
            />
          </Stepper.Step>

          {isMultiStage && (
            <Stepper.Step
              icon={<StepNumber isMultiStage={isMultiStage} baseIndex={1} />}
              label={<FormattedMessage id="deployments.step2" />}
            >
              <ModelSelection
                key="secondary-model"
                title={<FormattedMessage id="deployments.step2" />}
                initialSelectedModel={state.selectedSecondaryModel}
                modelType={MLModelType.Secondary}
                showBackButton
                onGoBack={goPrevStep}
                onContinue={handleSecondaryModelSelection}
              />
            </Stepper.Step>
          )}

          <Stepper.Step
            icon={<StepNumber isMultiStage={isMultiStage} baseIndex={2} />}
            label={<FormattedMessage id="deployments.step3" />}
          >
            <DeviceSelection
              initialSelectedDeviceIds={state.selectedDeviceIds}
              onContinue={handleDeviceSelectionChange}
              onGoBack={goPrevStep}
            />
          </Stepper.Step>

          <Stepper.Step
            icon={<StepNumber isMultiStage={isMultiStage} baseIndex={3} />}
            label={<FormattedMessage id="deployments.step4" />}
          >
            <InputSourceSelection
              initialInputSource={state.inputSource}
              deviceIds={state.selectedDeviceIds}
              initialSelectedCameras={state.selectedCameraStreams}
              initialMediaFolderConfig={state.mediaFolderConfig}
              onContinue={handleInputSourceSelection}
              onGoBack={goPrevStep}
            />
          </Stepper.Step>

          <Stepper.Step
            icon={<StepNumber isMultiStage={isMultiStage} baseIndex={4} />}
            label={<FormattedMessage id="deployments.step5" />}
          >
            <LogicSelection
              cameraStreams={state.selectedCameraStreams}
              savedStreamLogics={state.savedStreamLogics}
              inputSource={state.inputSource}
              onContinue={goNextStep}
              onGoBack={goPrevStep}
              onStreamLogicsSave={handleStreamLogicsSave}
            />
          </Stepper.Step>

          <Stepper.Step
            icon={<StepNumber isMultiStage={isMultiStage} baseIndex={5} />}
            label={<FormattedMessage id="deployments.step6" />}
          >
            <IntegrationSelection
              selectedDeviceIds={state.selectedDeviceIds}
              selectedCameraStreams={state.selectedCameraStreams}
              savedEventEndpoints={state.savedEventEndpoints}
              inputSource={state.inputSource}
              onGoBack={goPrevStep}
              onContinue={handleIntegrationsSave}
            />
          </Stepper.Step>

          <Stepper.Step
            icon={<StepNumber isMultiStage={isMultiStage} baseIndex={6} />}
            label={<FormattedMessage id="deployments.step7" />}
          >
            <DeploymentSettings
              initialConfidenceThresholds={state.confidenceThresholds}
              savedStreamLogics={state.savedStreamLogics}
              blurOptions={state.blurOptions}
              eventVideoDurationTime={state.eventVideoDurationTime}
              preEventSeconds={state.preEventSeconds}
              cacheSeconds={state.cacheSeconds}
              inferenceInterval={state.inferenceInterval}
              minDetectionFrames={state.minDetectionFrames}
              notificationSettings={state.notificationSettings}
              hasEmailAlertEnabled={hasEmailAlertEnabled}
              hasOCREnabled={hasOCREnabled}
              ocrSettings={state.ocrSettings}
              inputSource={state.inputSource}
              selectedDeviceIds={state.selectedDeviceIds}
              cameraDeviceIds={uniq(
                state.selectedCameraStreams.map((stream) => stream.device_id)
              )}
              initialGpuIndex={state.gpuIndex}
              initialRunInGroups={state.runInGroups}
              initialGpuSelection={state.gpuSelection}
              runningGpus={initialRunningGpus}
              onGoBack={goPrevStep}
              onContinue={handleSettingsSave}
            />
          </Stepper.Step>

          <Stepper.Step
            icon={<StepNumber isMultiStage={isMultiStage} baseIndex={7} />}
            label={<FormattedMessage id="deployments.step8" />}
          >
            <DeployPreview
              applicationType={application_type}
              primaryModel={state.selectedPrimaryModel}
              secondaryModel={state.selectedSecondaryModel}
              cameraStreams={state.selectedCameraStreams}
              savedStreamLogics={state.savedStreamLogics}
              savedEventEndpoints={state.savedEventEndpoints}
              inputSource={state.inputSource}
              selectedDeviceIds={state.selectedDeviceIds}
              mediaFolderConfig={state.mediaFolderConfig}
              runInGroups={state.runInGroups}
              plan={plan}
              isPlanLoading={isPlanLoading}
              planError={planError}
              devices={devices}
              onRefreshPlan={() => void refetchPlan()}
              onGoBack={goPrevStep}
              onDeployModel={() => void handleDeployModel()}
            />
          </Stepper.Step>
        </Stepper>
      </DeploymentStepperProvider>
    </Box>
  )
}
